import { Injectable } from '@angular/core';
import {
	CardInstrument,
	ErvInstrumentModel,
	GetPaymentInstrumentsResponse,
	PaymentAuth,
} from '@woolworthsnz/trader-api';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import equal from 'fast-deep-equal';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import LastUsedPaymentTypeEnum = GetPaymentInstrumentsResponse.LastUsedPaymentTypeEnum;
import { StatefulService } from './stateful.service';

dayjs.extend(isSameOrAfter);

export interface PaymentState {
	selectedPaymentType?: LastUsedPaymentTypeEnum;
	selectedInstrument?: CardInstrument;
	savedPaymentInstruments: CardInstrument[];
	directDebitEnabled: boolean;
	ervBalance?: ErvInstrumentModel;
	ervSelected?: boolean;
	instrumentsLoaded: boolean;
	informationalMessages?: string[];
	errorMessage?: string;
	cardProvider?: {
		authProvider?: PaymentAuth.ProviderEnum;
		authType?: PaymentAuth.TypeEnum;
	};
	authToken?: string;
	changeCardSelected?: boolean;
	error?: {
		errorMessage: string | undefined | null;
		errorTitle: string | undefined | null;
	};
}

const initialState: PaymentState = {
	selectedPaymentType: LastUsedPaymentTypeEnum.CreditCard,
	selectedInstrument: undefined,
	savedPaymentInstruments: [],
	directDebitEnabled: false,
	ervSelected: false,
	instrumentsLoaded: false,
	changeCardSelected: false,
};

@Injectable({ providedIn: 'root' })
export class PaymentStoreService extends StatefulService<PaymentState> {
	constructor() {
		super(initialState);
	}

	get distinctSelectedState$(): Observable<{
		selectedPaymentType: GetPaymentInstrumentsResponse.LastUsedPaymentTypeEnum | undefined;
		selectedInstrumentId: string | undefined;
		directDebitEnabled: boolean;
	}> {
		return this.state$.pipe(
			map((state) => ({
				selectedPaymentType: state.selectedPaymentType,
				selectedInstrumentId: state.selectedInstrument?.id,
				directDebitEnabled: state.directDebitEnabled,
			})),
			distinctUntilChanged((a, b) => equal(a, b))
		);
	}

	get hasSavedCards$(): Observable<boolean> {
		return this.select('savedPaymentInstruments').pipe(map((res) => res?.length > 0));
	}

	get hasSavedInstruments$(): Observable<boolean> {
		return combineLatest([this.hasSavedCards$, this.select('directDebitEnabled')]).pipe(
			map(([hasSavedCards, hasDirectDebit]) => hasSavedCards || hasDirectDebit)
		);
	}

	get paymentProvider$(): Observable<PaymentAuth.ProviderEnum | undefined> {
		return this.select('cardProvider').pipe(map((res) => res?.authProvider));
	}

	get changeCardSelected$(): Observable<boolean | undefined> {
		return this.select('changeCardSelected').pipe(map((res) => res));
	}

	public clearCurrentInstrument(changeCardSelected = false): void {
		this.setState({
			selectedPaymentType: LastUsedPaymentTypeEnum.CreditCard,
			selectedInstrument: undefined,
			changeCardSelected: changeCardSelected,
		});
	}

	public setInstrument(instrument: CardInstrument, changeCardSelected = false): void {
		this.setState({
			selectedPaymentType: LastUsedPaymentTypeEnum.CreditCard,
			selectedInstrument: instrument,
			changeCardSelected: changeCardSelected,
		});
	}

	public setInstruments(instruments: {
		cardInstruments: CardInstrument[];
		directDebitEnabled: boolean;
		ervBalance?: ErvInstrumentModel;
		lastUsedPaymentType?: LastUsedPaymentTypeEnum;
		selectedInstrumentId?: string;
	}): void {
		const { cardInstruments, directDebitEnabled, lastUsedPaymentType, ervBalance, selectedInstrumentId } =
			instruments;
		let selected: CardInstrument | undefined;

		if (selectedInstrumentId) {
			// Preselect instrument, if not found assume its a new card that's not being saved
			selected = cardInstruments.find((i) => i.id === selectedInstrumentId);
		} else {
			// Persist selected card by ID if possible (iframe url may update between calls)
			selected = cardInstruments.find((i) => i.id === this.state.selectedInstrument?.id);

			if (!selected) {
				selected = cardInstruments.find((i) => i.primary);
			}
			if (!selected) {
				selected = cardInstruments.length
					? this.sortCreditCardsByLastUsedDate(cardInstruments)[cardInstruments.length - 1]
					: undefined;
			}
		}

		this.setState({
			selectedPaymentType: lastUsedPaymentType ?? LastUsedPaymentTypeEnum.CreditCard,
			selectedInstrument: selected,
			savedPaymentInstruments: cardInstruments,
			ervBalance,
			directDebitEnabled,
			instrumentsLoaded: true,
		});
	}

	public setDirectDebit(): void {
		this.setState({
			selectedPaymentType: LastUsedPaymentTypeEnum.DirectDebit,
		});
	}

	public setCard(): void {
		this.setState({
			selectedPaymentType: LastUsedPaymentTypeEnum.CreditCard,
		});
	}

	public setProvider(cardProvider: {
		authProvider?: PaymentAuth.ProviderEnum;
		authType?: PaymentAuth.TypeEnum;
	}): void {
		const { authProvider, authType } = cardProvider;
		this.setState({
			cardProvider: {
				authProvider: authProvider,
				authType: authType,
			},
		});
	}

	public setAuthToken(token?: string): void {
		this.setState({
			authToken: token,
		});
	}

	public setErrorMessage(message?: string): void {
		this.setState({ errorMessage: message });
	}

	public setErrorWithErrorTitle(message?: string, errorTitle?: string): void {
		this.setState({ error: { errorMessage: message, errorTitle: errorTitle } });
	}

	public clearErrors(): void {
		this.setState({
			errorMessage: '',
			error: undefined,
		});
	}

	private sortCreditCardsByLastUsedDate(creditCards: CardInstrument[]): CardInstrument[] {
		const minDate = dayjs('1970-01-01');

		return creditCards.sort((a, b) =>
			dayjs(a.lastUsed || minDate).isSameOrAfter(dayjs(b.lastUsed || minDate)) ? 1 : -1
		);
	}
}
